// src/realtime/realtime-provider.jsx

import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { socketClient } from "./socket-client";
import api, {refreshAuthSession} from "../api";
import {isCurrentSessionRevocation} from "./session-events";
import {memberIsOnline, normalizeWorkspacePresence} from "./workspace-presence";

export const RealtimeContext = createContext(null);

const DIRECT_PUSH_EVENTS = [
    "user-event-log.created",
    "user-event-log.updated",
    "user-event-log.deleted",
    "user-role.created",
    "user-role.updated",
    "user-role.deleted",
    "workspace-membership.removed",
    "workspace.archived",
    "session.created",
    "session.revoked",
    "session.list-changed",
];

const ACCESS_TOKEN_REFRESH_MARGIN_MS = 60 * 1000;
const REALTIME_DUPLICATE_WINDOW_MS = 2 * 1000;

export function realtimeEventFingerprint(event, fallbackType = null) {
    if (!event || typeof event !== "object") return null;
    const type = event.type || fallbackType;
    const payload = event.payload ?? event.data ?? event;
    const entity = payload?.entity ?? payload?.session ?? payload;
    const version = entity?.version ?? entity?.updatedAt ?? entity?.createdAt ?? event.sentAt ?? event.eventId;
    if (!type || version === null || version === undefined) return null;

    return [
        type,
        event.workspaceId ?? event.workspaceDocumentId ?? "",
        event.uid ?? event.contentType ?? event.scopeType ?? "",
        event.documentId ?? entity?.documentId ?? entity?.id ?? "",
        event.action ?? "",
        version,
    ].map(String).join(":");
}

function accessTokenExpiresAt(token) {
    try {
        const encoded = String(token || "").split(".")[1]
            .replace(/-/g, "+")
            .replace(/_/g, "/");
        const padded = encoded.padEnd(Math.ceil(encoded.length / 4) * 4, "=");
        const payload = JSON.parse(window.atob(padded));
        return Number(payload.exp || 0) * 1000 || null;
    } catch (_) {
        return null;
    }
}

export function RealtimeProvider({ children, token, currentSessionId, workspaceId, workspaceDocumentId, onSessionCreated, onSessionInvalidated }) {
    const [status, setStatus] = useState("idle");
    const [lastEvent, setLastEvent] = useState(null);
    const [eventQueue, setEventQueue] = useState([]);
    const [workspacePresence, setWorkspacePresence] = useState([]);
    const eventSequenceRef = useRef(0);
    const recentEventFingerprintsRef = useRef(new Map());

    const publishEvent = useCallback((event, fallbackType) => {
        const payload = event && typeof event === "object" ? event : {data: event};
        const normalizedEvent = payload?.type || !fallbackType
            ? payload
            : {...payload, type: fallbackType};
        const fingerprint = realtimeEventFingerprint(normalizedEvent, fallbackType);
        if (fingerprint) {
            const now = Date.now();
            const lastSeenAt = recentEventFingerprintsRef.current.get(fingerprint);
            if (lastSeenAt && now - lastSeenAt < REALTIME_DUPLICATE_WINDOW_MS) return;
            recentEventFingerprintsRef.current.set(fingerprint, now);
            if (recentEventFingerprintsRef.current.size > 300) {
                for (const [key, seenAt] of recentEventFingerprintsRef.current) {
                    if (now - seenAt >= REALTIME_DUPLICATE_WINDOW_MS) {
                        recentEventFingerprintsRef.current.delete(key);
                    }
                }
            }
        }
        const queuedEvent = {
            ...normalizedEvent,
            __realtimeSequence: ++eventSequenceRef.current,
            __realtimeWorkspaceId: workspaceId,
        };

        setLastEvent(queuedEvent);
        setEventQueue((current) => [...current, queuedEvent].slice(-200));
    }, [workspaceId]);

    useEffect(() => {
        const expiresAt = accessTokenExpiresAt(token);
        if (!expiresAt) return;

        const refresh = () => {
            refreshAuthSession().catch(() => onSessionInvalidated?.("session_expired"));
        };
        const delay = Math.max(0, expiresAt - Date.now() - ACCESS_TOKEN_REFRESH_MARGIN_MS);
        const timer = window.setTimeout(refresh, delay);
        return () => window.clearTimeout(timer);
    }, [onSessionInvalidated, token]);

    useEffect(() => {
        if (!token || !workspaceId) {
            socketClient.disconnect();
            setStatus("idle");
            setLastEvent(null);
            setEventQueue([]);
            setWorkspacePresence([]);
            return;
        }

        setLastEvent(null);
        setEventQueue([]);
        setWorkspacePresence([]);
        setStatus("connecting");

        const socket = socketClient.connect({
            token,
            workspaceId,
            workspaceDocumentId,
        });

        if (!socket) {
            setStatus("error");
            return;
        }

        const handleConnect = () => {
            setStatus("connected");
            socket.emit("workspace-presence:get");
        };

        const handleDisconnect = (reason) => {
            setStatus("disconnected");
            setWorkspacePresence([]);
            if (reason === "io server disconnect" && token) {
                api.get("/user-sessions/current", {suppressGlobalErrorHandler: true}).catch(() => {});
            }
        };

        const handleConnectError = (error) => {
            console.error("[RealtimeProvider] connect error:", {
                message: error.message,
                code: error.data?.code || null,
            });
            setStatus("error");
            if (error.data?.code === "session_inactive") {
                onSessionInvalidated?.("session_expired");
            }
            else if (error.data?.code === "invalid_token") {
                refreshAuthSession().catch(() => onSessionInvalidated?.("session_expired"));
            }
        };

        const handleContentChanged = (event) => {
            // console.log("[RealtimeProvider] content.changed", event);
            publishEvent(event);
        };

        const handleDirectPush = (event) => {
            publishEvent(event);
        };

        const handleSessionRevoked = (event) => {
            if (!isCurrentSessionRevocation(event, currentSessionId)) return;
            onSessionInvalidated?.(event?.payload?.reason || "revoked_by_other_device");
        };

        const handleSessionCreated = (event) => {
            const session = event?.payload?.session || null;
            window.dispatchEvent(new CustomEvent("platform:session-created", {detail: session}));
            onSessionCreated?.(session);
        };

        const handleSessionListChanged = (event) => {
            window.dispatchEvent(new CustomEvent("platform:session-list-changed", {detail: event?.payload || null}));
        };

        const handleWorkspacePresence = (event) => {
            const eventWorkspaceKeys = [event?.workspaceId, event?.workspaceDocumentId].filter(Boolean).map(String);
            const activeWorkspaceKeys = [workspaceId, workspaceDocumentId].filter(Boolean).map(String);
            if (!eventWorkspaceKeys.some((key) => activeWorkspaceKeys.includes(key))) return;
            setWorkspacePresence(normalizeWorkspacePresence(event?.memberships));
        };

        // The socket server emits user-targeted pushes using their concrete
        // event type. Keep `strapi:push` as a backwards-compatible channel.
        const typedDirectHandlers = DIRECT_PUSH_EVENTS.map((eventType) => {
            const handler = (event) => {
                publishEvent(event, eventType);
            };
            socket.on(eventType, handler);
            return [eventType, handler];
        });

        socket.on("connect", handleConnect);
        socket.on("disconnect", handleDisconnect);
        socket.on("connect_error", handleConnectError);
        socket.on("content.changed", handleContentChanged);
        socket.on("strapi:push", handleDirectPush);
        socket.on("session.created", handleSessionCreated);
        socket.on("session.revoked", handleSessionRevoked);
        socket.on("session.list-changed", handleSessionListChanged);
        socket.on("workspace-presence.snapshot", handleWorkspacePresence);
        if (socket.connected) handleConnect();

        return () => {
            socket.off("connect", handleConnect);
            socket.off("disconnect", handleDisconnect);
            socket.off("connect_error", handleConnectError);
            socket.off("content.changed", handleContentChanged);
            socket.off("strapi:push", handleDirectPush);
            socket.off("session.created", handleSessionCreated);
            socket.off("session.revoked", handleSessionRevoked);
            socket.off("session.list-changed", handleSessionListChanged);
            socket.off("workspace-presence.snapshot", handleWorkspacePresence);
            typedDirectHandlers.forEach(([eventType, handler]) => socket.off(eventType, handler));
        };
    }, [currentSessionId, onSessionCreated, onSessionInvalidated, publishEvent, token, workspaceDocumentId, workspaceId]);

    const value = useMemo(() => {
        return {
            socket: socketClient.getSocket(),
            status,
            isConnected: status === "connected",
            lastEvent,
            eventQueue,
            workspacePresence,
            isMemberOnline: (memberData) => memberIsOnline(memberData, workspacePresence),
            emit: socketClient.emit.bind(socketClient),
        };
    }, [eventQueue, status, lastEvent, workspacePresence]);

    return (
        <RealtimeContext.Provider value={value}>
            {children}
        </RealtimeContext.Provider>
    );
}
